import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Calendar, UnlockKeyhole, Pencil, Trash2, ChevronLeft, ChevronRight, Users, CheckSquare, XSquare } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks } from 'date-fns';
import { es } from 'date-fns/locale';

function AdminTrainings() {
  const [trainings, setTrainings] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [weekDialogOpen, setWeekDialogOpen] = useState(false);
  const [openSlotsDialogOpen, setOpenSlotsDialogOpen] = useState(false);
  const [openSlotsRange, setOpenSlotsRange] = useState({
    fecha_inicio: format(new Date(), 'yyyy-MM-dd'),
    fecha_fin: format(addWeeks(new Date(), 1), 'yyyy-MM-dd')
  });
  const [formData, setFormData] = useState({
    tipo: 'carrera',
    dia_semana: 'lunes',
    fecha: '',
    hora: '18:30',
    ejercicios: [],
    max_participantes: 12
  });
  const [weeklyConfig, setWeeklyConfig] = useState({
    hora: '18:30',
    max_participantes: 12,
    mes_seleccionado: new Date(),
    dias: [
      { dia: 'lunes', tipo: 'carrera', activo: true },
      { dia: 'martes', tipo: 'funcional', activo: true },
      { dia: 'miercoles', tipo: 'funcional', activo: false },
      { dia: 'jueves', tipo: 'funcional', activo: true },
      { dia: 'viernes', tipo: 'competicion', activo: false },
      { dia: 'sabado', tipo: 'excursion', activo: false },
      { dia: 'domingo', tipo: 'especial', activo: false }
    ]
  });
  const [editingTraining, setEditingTraining] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selectedTrainings, setSelectedTrainings] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Filtrar entrenamientos por el mes seleccionado
  const filteredTrainings = trainings.filter(training => {
    if (!training.fecha) return false;
    const parts = training.fecha.split('-');
    if (parts.length !== 3) return false;
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const targetMonth = viewMonth.getMonth() + 1;
    const targetYear = viewMonth.getFullYear();
    return month === targetMonth && year === targetYear;
  }).sort((a, b) => a.fecha.localeCompare(b.fecha));

  const fetchData = async () => {
    try {
      const trainingsRes = await axios.get(`${API}/trainings`);
      setTrainings(trainingsRes.data);
      // Exercises endpoint was removed, using entrenos instead
      setExercises([]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trainings`, formData);
      fetchData();
      resetForm();
      setDialogOpen(false);
      alert('Entreno creado correctamente');
    } catch (error) {
      alert('Error al crear entreno');
    }
  };

  const handleOpenWeeklySlots = async () => {
    try {
      const params = new URLSearchParams({
        fecha_inicio: openSlotsRange.fecha_inicio,
        fecha_fin: openSlotsRange.fecha_fin
      });
      await axios.post(`${API}/trainings/open-weekly?${params}`);
      fetchData();
      setOpenSlotsDialogOpen(false);
      alert(`Plazas abiertas del ${openSlotsRange.fecha_inicio} al ${openSlotsRange.fecha_fin}`);
    } catch (error) {
      alert('Error al abrir plazas');
    }
  };

  const handleCreateWeeklyTrainings = async () => {
    // Usar el mes seleccionado
    const selectedDate = weeklyConfig.mes_seleccionado;
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const diasActivos = weeklyConfig.dias.filter(d => d.activo);
    
    if (diasActivos.length === 0) {
      alert('Debes seleccionar al menos un día');
      return;
    }

    try {
      const dayNameToNumber = {
        'lunes': 1,
        'martes': 2,
        'miercoles': 3,
        'jueves': 4,
        'viernes': 5,
        'sabado': 6,
        'domingo': 0
      };

      let createdCount = 0;

      // Recorrer todos los días del mes seleccionado
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dayOfWeek = currentDate.getDay();
        
        // Verificar si este día de la semana está en la configuración
        const diaConfig = diasActivos.find(d => dayNameToNumber[d.dia] === dayOfWeek);
        
        if (diaConfig) {
          const fecha = format(currentDate, 'yyyy-MM-dd');
          
          // Verificar si ya existe un entreno en esa fecha
          const exists = trainings.some(t => t.fecha === fecha && t.tipo === diaConfig.tipo);
          
          if (!exists) {
            await axios.post(`${API}/trainings`, {
              tipo: diaConfig.tipo,
              dia_semana: diaConfig.dia,
              fecha: fecha,
              hora: weeklyConfig.hora,
              ejercicios: []
            });
            createdCount++;
          }
        }
      }
      
      fetchData();
      setWeekDialogOpen(false);
      alert(`Se crearon ${createdCount} entrenamientos para ${format(selectedDate, 'MMMM yyyy', { locale: es })}`);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al crear entrenamientos del mes');
    }
  };

  const handleEditTraining = (training) => {
    setEditingTraining(training);
    setFormData({
      tipo: training.tipo,
      dia_semana: training.dia_semana,
      fecha: training.fecha,
      hora: training.hora,
      ejercicios: training.ejercicios || [],
      max_participantes: training.max_participantes || 12
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTraining = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/trainings/${editingTraining.id}`, formData);
      fetchData();
      setEditDialogOpen(false);
      setEditingTraining(null);
      alert('Entreno actualizado correctamente');
    } catch (error) {
      alert('Error al actualizar entreno');
    }
  };

  const handleDeleteTraining = async (trainingId, fecha) => {
    let fechaFormateada = fecha;
    try {
      fechaFormateada = format(new Date(fecha + 'T00:00:00'), "d 'de' MMMM", { locale: es });
    } catch (e) {
      console.error('Error formateando fecha:', e);
    }
    
    if (window.confirm(`¿Estás seguro de eliminar el entreno del ${fechaFormateada}?`)) {
      try {
        const response = await axios.delete(`${API}/trainings/${trainingId}`);
        console.log('Entreno eliminado:', response.data);
        await fetchData();
        alert('Entreno eliminado correctamente');
      } catch (error) {
        console.error('Error al eliminar:', error.response?.data || error.message);
        alert('Error al eliminar entreno: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // Funciones para eliminación en lote
  const toggleTrainingSelection = (trainingId) => {
    const newSelected = new Set(selectedTrainings);
    if (newSelected.has(trainingId)) {
      newSelected.delete(trainingId);
    } else {
      newSelected.add(trainingId);
    }
    setSelectedTrainings(newSelected);
  };

  const selectAllInMonth = () => {
    const ids = filteredTrainings.map(t => t.id);
    setSelectedTrainings(new Set(ids));
  };

  const clearSelection = () => {
    setSelectedTrainings(new Set());
    setIsSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedTrainings.size === 0) {
      alert('No hay entrenamientos seleccionados');
      return;
    }

    const count = selectedTrainings.size;
    if (window.confirm(`¿Estás seguro de eliminar ${count} entrenamiento${count > 1 ? 's' : ''}?`)) {
      setBulkDeleteLoading(true);
      try {
        const response = await axios.post(`${API}/trainings/bulk-delete`, {
          training_ids: Array.from(selectedTrainings)
        });
        await fetchData();
        clearSelection();
        alert(response.data.message);
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar entrenamientos: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkDeleteLoading(false);
      }
    }
  };

  const handleDeleteMonth = async () => {
    const monthStart = format(startOfMonth(viewMonth), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(viewMonth), 'yyyy-MM-dd');
    const monthName = format(viewMonth, 'MMMM yyyy', { locale: es });

    if (window.confirm(`¿Estás seguro de eliminar TODOS los entrenamientos de ${monthName}?\n\nEsta acción no se puede deshacer.`)) {
      setBulkDeleteLoading(true);
      try {
        const response = await axios.post(`${API}/trainings/bulk-delete`, {
          fecha_desde: monthStart,
          fecha_hasta: monthEnd
        });
        await fetchData();
        clearSelection();
        alert(response.data.message);
      } catch (error) {
        console.error('Error al eliminar:', error);
        alert('Error al eliminar entrenamientos: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkDeleteLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'carrera',
      dia_semana: 'lunes',
      fecha: '',
      hora: '19:00',
      ejercicios: [],
      max_participantes: 12
    });
  };

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="admin-trainings-title">
              Administración de Entrenamientos
            </h1>
            <p className="text-gray-400">Gestiona los entrenamientos y plazas</p>
            <p className="text-gray-500 text-sm mt-1">
              ⏰ Las plazas se abrirán automáticamente los domingos a las 20:00
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Botones de selección múltiple */}
            {!isSelectionMode ? (
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                onClick={() => setIsSelectionMode(true)}
                data-testid="btn-selection-mode"
              >
                <CheckSquare className="mr-2 h-4 w-4" />
                Seleccionar
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                  onClick={selectAllInMonth}
                  data-testid="btn-select-all"
                >
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Todo el mes ({filteredTrainings.length})
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  onClick={clearSelection}
                  data-testid="btn-clear-selection"
                >
                  <XSquare className="mr-2 h-4 w-4" />
                  Cancelar
                </Button>
                {selectedTrainings.size > 0 && (
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading}
                    data-testid="btn-delete-selected"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {bulkDeleteLoading ? 'Eliminando...' : `Eliminar (${selectedTrainings.size})`}
                  </Button>
                )}
              </>
            )}

            {/* Botón eliminar mes completo */}
            {filteredTrainings.length > 0 && !isSelectionMode && (
              <Button
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-900/30"
                onClick={handleDeleteMonth}
                disabled={bulkDeleteLoading}
                data-testid="btn-delete-month"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {bulkDeleteLoading ? 'Eliminando...' : 'Borrar Mes'}
              </Button>
            )}

            <Dialog open={weekDialogOpen} onOpenChange={setWeekDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="btn-primary-mk"
                  data-testid="btn-create-weekly"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Crear Mes Completo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-gold">Configurar Entrenamientos Mensuales</DialogTitle>
                  <p className="text-gray-400 text-sm mt-2">
                    Se crearán entrenamientos para todo el mes según tu configuración
                  </p>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Selector de mes */}
                  <div className="space-y-2">
                    <Label className="text-lg">Seleccionar Mes</Label>
                    <div className="flex items-center justify-center gap-4 p-3 bg-gray-800 rounded-lg">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWeeklyConfig({
                          ...weeklyConfig,
                          mes_seleccionado: subMonths(weeklyConfig.mes_seleccionado, 1)
                        })}
                        className="text-white hover:text-gold"
                        data-testid="btn-mes-anterior"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <span className="text-xl font-bold text-gold capitalize min-w-[180px] text-center">
                        {format(weeklyConfig.mes_seleccionado, 'MMMM yyyy', { locale: es })}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWeeklyConfig({
                          ...weeklyConfig,
                          mes_seleccionado: addMonths(weeklyConfig.mes_seleccionado, 1)
                        })}
                        className="text-white hover:text-gold"
                        data-testid="btn-mes-siguiente"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hora por Defecto</Label>
                      <Input
                        type="time"
                        value={weeklyConfig.hora}
                        onChange={(e) => setWeeklyConfig({ ...weeklyConfig, hora: e.target.value })}
                        className="bg-gray-800 border-gray-700 text-white"
                        data-testid="input-hora-semanal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Máximo Participantes</Label>
                      <Input
                        type="number"
                        value={weeklyConfig.max_participantes}
                        onChange={(e) => setWeeklyConfig({ ...weeklyConfig, max_participantes: parseInt(e.target.value) })}
                        className="bg-gray-800 border-gray-700 text-white"
                        min="1"
                        max="30"
                        data-testid="input-max-participantes"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label className="text-lg">Días de la Semana</Label>
                    {weeklyConfig.dias.map((dia, index) => (
                      <div key={dia.dia} className="flex items-center gap-4 p-3 bg-gray-800 rounded-lg">
                        <input
                          type="checkbox"
                          checked={dia.activo}
                          onChange={(e) => {
                            const newDias = [...weeklyConfig.dias];
                            newDias[index].activo = e.target.checked;
                            setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                          }}
                          className="w-5 h-5 cursor-pointer"
                          data-testid={`checkbox-${dia.dia}`}
                        />
                        <span className="capitalize text-white font-semibold w-24">{dia.dia}</span>
                        <Select
                          value={dia.tipo}
                          onValueChange={(value) => {
                            const newDias = [...weeklyConfig.dias];
                            newDias[index].tipo = value;
                            setWeeklyConfig({ ...weeklyConfig, dias: newDias });
                          }}
                          disabled={!dia.activo}
                        >
                          <SelectTrigger className="bg-gray-900 border-gray-700 w-full" data-testid={`select-tipo-${dia.dia}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700 text-white">
                            <SelectItem value="carrera">Carrera</SelectItem>
                            <SelectItem value="funcional">Funcional</SelectItem>
                            <SelectItem value="competicion">Competición</SelectItem>
                            <SelectItem value="especial">Especial</SelectItem>
                            <SelectItem value="excursion">Excursión</SelectItem>
                            <SelectItem value="natacion">Natación</SelectItem>
                            <SelectItem value="bike">Bike</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={handleCreateWeeklyTrainings}
                    className="w-full btn-primary-mk"
                    data-testid="btn-confirmar-semana"
                  >
                    Crear Entrenamientos Seleccionados
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Dialog para Abrir Plazas con selector de semana */}
            <Dialog open={openSlotsDialogOpen} onOpenChange={setOpenSlotsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="btn-open-slots"
                >
                  <UnlockKeyhole className="mr-2 h-4 w-4" />
                  Abrir Plazas
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-gold">Abrir Plazas para Inscripciones</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-gray-400 text-sm">
                    Selecciona el rango de fechas para abrir las inscripciones:
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Desde</Label>
                      <Input
                        type="date"
                        value={openSlotsRange.fecha_inicio}
                        onChange={(e) => setOpenSlotsRange({...openSlotsRange, fecha_inicio: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hasta</Label>
                      <Input
                        type="date"
                        value={openSlotsRange.fecha_fin}
                        onChange={(e) => setOpenSlotsRange({...openSlotsRange, fecha_fin: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                  {/* Botones de acceso rápido */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        const nextWeek = addWeeks(today, 1);
                        setOpenSlotsRange({
                          fecha_inicio: format(today, 'yyyy-MM-dd'),
                          fecha_fin: format(nextWeek, 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Próximos 7 días
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        const start = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
                        const end = endOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
                        setOpenSlotsRange({
                          fecha_inicio: format(start, 'yyyy-MM-dd'),
                          fecha_fin: format(end, 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Semana que viene
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-gray-600"
                      onClick={() => {
                        const today = new Date();
                        setOpenSlotsRange({
                          fecha_inicio: format(startOfMonth(today), 'yyyy-MM-dd'),
                          fecha_fin: format(endOfMonth(today), 'yyyy-MM-dd')
                        });
                      }}
                    >
                      Este mes
                    </Button>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handleOpenWeeklySlots}
                  >
                    <UnlockKeyhole className="mr-2 h-4 w-4" />
                    Abrir Plazas
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary-mk" data-testid="btn-add-training">
                  <Plus className="mr-2 h-4 w-4" /> Nuevo Entreno
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 text-white border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-gold">Crear Nuevo Entreno</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo de Entreno</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="carrera">Carrera</SelectItem>
                        <SelectItem value="funcional">Funcional</SelectItem>
                        <SelectItem value="competicion">Competición</SelectItem>
                        <SelectItem value="especial">Especial</SelectItem>
                        <SelectItem value="excursion">Excursión</SelectItem>
                        <SelectItem value="natacion">Natación</SelectItem>
                        <SelectItem value="bike">Bike</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dia_semana">Día de la Semana</Label>
                    <Select
                      value={formData.dia_semana}
                      onValueChange={(value) => setFormData({ ...formData, dia_semana: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-dia">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        <SelectItem value="lunes">Lunes</SelectItem>
                        <SelectItem value="martes">Martes</SelectItem>
                        <SelectItem value="miercoles">Miércoles</SelectItem>
                        <SelectItem value="jueves">Jueves</SelectItem>
                        <SelectItem value="viernes">Viernes</SelectItem>
                        <SelectItem value="sabado">Sábado</SelectItem>
                        <SelectItem value="domingo">Domingo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fecha">Fecha</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-fecha"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hora">Hora</Label>
                    <Input
                      id="hora"
                      type="time"
                      value={formData.hora}
                      onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-hora"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_participantes">Máx. Participantes</Label>
                    <Input
                      id="max_participantes"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.max_participantes}
                      onChange={(e) => setFormData({ ...formData, max_participantes: parseInt(e.target.value) || 12 })}
                      className="bg-gray-800 border-gray-700 text-white"
                      data-testid="input-max-participantes"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary-mk" data-testid="btn-save-training">
                    Crear Entreno
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Selector de mes para la vista */}
            <div className="flex items-center justify-center gap-4 mb-8 p-4 bg-gray-800/50 rounded-xl">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                className="text-white hover:text-gold hover:bg-gray-700"
                data-testid="btn-view-mes-anterior"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <span className="text-2xl font-bold text-gold capitalize min-w-[200px] text-center">
                {format(viewMonth, 'MMMM yyyy', { locale: es })}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className="text-white hover:text-gold hover:bg-gray-700"
                data-testid="btn-view-mes-siguiente"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>

            {filteredTrainings.length === 0 ? (
              <div className="text-center py-20">
                <Calendar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No hay entrenamientos para {format(viewMonth, 'MMMM yyyy', { locale: es })}</p>
                <p className="text-gray-500 text-sm mt-2">Usa &ldquo;Crear Mes Completo&rdquo; para añadir entrenamientos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredTrainings.map((training) => (
                  <Card 
                    key={training.id} 
                    className={`card-mk hover:border-gold/50 transition-colors cursor-pointer ${
                      selectedTrainings.has(training.id) ? 'ring-2 ring-gold border-gold' : ''
                    }`}
                    data-testid={`admin-training-card-${training.id}`}
                    onClick={() => isSelectionMode && toggleTrainingSelection(training.id)}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          {isSelectionMode && (
                            <Checkbox
                              checked={selectedTrainings.has(training.id)}
                              onCheckedChange={() => toggleTrainingSelection(training.id)}
                              className="mt-1 border-gray-500 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                              data-testid={`checkbox-training-${training.id}`}
                            />
                          )}
                          <div>
                            <span className="capitalize text-lg">{training.dia_semana}</span>
                            <div className="text-sm text-gold font-normal mt-1">
                              {format(new Date(training.fecha + 'T00:00:00'), "d 'de' MMMM", { locale: es })}
                            </div>
                          </div>
                        </div>
                        {!isSelectionMode && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleEditTraining(training)}
                              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gold hover:bg-gray-700 rounded"
                              data-testid={`btn-edit-training-${training.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteTraining(training.id, training.fecha)}
                              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded"
                              data-testid={`btn-delete-training-${training.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-0">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Tipo:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          training.tipo === 'carrera' ? 'bg-blue-500/20 text-blue-400' :
                          training.tipo === 'funcional' ? 'bg-yellow-500/20 text-yellow-400' :
                          training.tipo === 'competicion' ? 'bg-red-500/20 text-red-400' :
                          training.tipo === 'especial' ? 'bg-purple-500/20 text-purple-400' :
                          training.tipo === 'excursion' ? 'bg-green-500/20 text-green-400' :
                          training.tipo === 'natacion' ? 'bg-cyan-500/20 text-cyan-400' :
                          training.tipo === 'bike' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {training.tipo === 'natacion' ? 'Natación' : training.tipo === 'bike' ? 'Bike' : training.tipo}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Hora:</span>
                        <span className="text-white">{training.hora}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Participantes:</span>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-gray-400" />
                          <span className={`${training.participantes.length >= training.max_participantes ? 'text-red-400' : 'text-white'}`}>
                            {training.participantes.length}/{training.max_participantes}
                          </span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-gray-700">
                        {training.plazas_abiertas ? (
                          <span className="text-xs text-green-500 flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Plazas abiertas
                          </span>
                        ) : (
                          <span className="text-xs text-red-500 flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            Plazas cerradas
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Diálogo de edición */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) {
            setEditingTraining(null);
            resetForm();
          }
        }}>
          <DialogContent className="bg-gray-900 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle className="text-gold">
                Editar Entreno - {editingTraining && format(new Date(editingTraining.fecha), "d 'de' MMMM", { locale: es })}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateTraining} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tipo">Tipo de Entreno</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="edit-select-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="carrera">Carrera</SelectItem>
                    <SelectItem value="funcional">Funcional</SelectItem>
                    <SelectItem value="competicion">Competición</SelectItem>
                    <SelectItem value="especial">Especial</SelectItem>
                    <SelectItem value="excursion">Excursión</SelectItem>
                    <SelectItem value="natacion">Natación</SelectItem>
                    <SelectItem value="bike">Bike</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-hora">Hora</Label>
                <Input
                  id="edit-hora"
                  type="time"
                  value={formData.hora}
                  onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-hora"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-fecha">Fecha</Label>
                <Input
                  id="edit-fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  required
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-fecha"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-max-participantes">Máx. Participantes</Label>
                <Input
                  id="edit-max-participantes"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_participantes}
                  onChange={(e) => setFormData({ ...formData, max_participantes: parseInt(e.target.value) || 12 })}
                  className="bg-gray-800 border-gray-700 text-white"
                  data-testid="edit-input-max-participantes"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)}
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  data-testid="btn-cancel-edit"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 btn-primary-mk" data-testid="btn-save-edit">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default AdminTrainings;
